import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';

import { useUserContextStore } from '../../store/user-context.store';
import { resolveAuth0Client } from './auth0/client';
import { createAuthSessionOrchestrator } from './session/orchestrator';
import { createAuthSessionState } from './session/auth-session-state';
import { AUTH_SESSION_TOKEN_EXPIRY_BUFFER_MS } from './session/session-deadline';
import type {
  Auth0ClientLike,
  AuthSessionStatus,
  EnsureSessionOptions,
  EnsureSessionResult,
} from './session/types';

export type {
  Auth0ClientLike,
  AuthSessionStatus,
  EnsureSessionOptions,
  EnsureSessionResult,
} from './session/types';

export const useAuthSessionStore = defineStore('auth-session', () => {
  const sessionSyncPromises = new Map<string, Promise<EnsureSessionResult>>();
  let mfaRedirectPromise: Promise<void> | null = null;

  const userContextStore = useUserContextStore();

  // State
  const status = ref<AuthSessionStatus>('unknown');
  const syncError = ref<unknown>(null);
  const jwtToken = ref<string | null>(null);
  const idToken = ref<string | null>(null);
  const lastActiveTime = ref(Date.now());
  const sessionActiveUntil = ref<number | null>(null);
  const tokenExpiresAt = ref<number | null>(null);
  const ifExternalUserId = ref<string | null>(null);
  const ifClientId = ref<string | null>(null);
  const givenName = ref<string | null>(null);
  const familyName = ref<string | null>(null);
  const organizationId = ref<string | null>(null);

  // Derived session state
  const isTokenCurrentlyExpired = () => {
    if (!tokenExpiresAt.value) return true;
    // Treat tokens as expired shortly before actual expiry to avoid racing requests at the boundary.
    return Date.now() >= tokenExpiresAt.value - AUTH_SESSION_TOKEN_EXPIRY_BUFFER_MS;
  };
  const isTokenExpired = computed(isTokenCurrentlyExpired);

  const isLoggedIn = computed(() => !!jwtToken.value && !!idToken.value && !isTokenExpired.value);

  const hasValidToken = computed(() => isLoggedIn.value);
  const isReady = computed(() => status.value === 'ready');

  const isAuthenticatedInIf = computed(() => !!ifExternalUserId.value && !!ifClientId.value);
  const requiresBasicInfoCollection = computed(() => {
    return givenName.value === null && familyName.value === null;
  });
  const requiredOrganizationId = computed(() => {
    if (!userContextStore.isLoaded) {
      return null;
    }

    return userContextStore.resolvedOrganizationId;
  });
  const needsOrganizationScopedSession = computed(() => {
    const nextRequiredOrganizationId = requiredOrganizationId.value;

    if (!nextRequiredOrganizationId) {
      return false;
    }

    return organizationId.value !== nextRequiredOrganizationId;
  });
  const isOrganizationScoped = computed(() => {
    if (
      !userContextStore.isLoaded ||
      userContextStore.requiresOrganizationCreation ||
      userContextStore.requiresOrganizationSelection
    ) {
      return false;
    }

    if (!requiredOrganizationId.value) {
      return true;
    }

    return !needsOrganizationScopedSession.value;
  });
  const requiresOrganizationSelection = computed(() => {
    return userContextStore.requiresOrganizationSelection;
  });
  const requiresOrganizationCreation = computed(() => {
    return userContextStore.requiresOrganizationCreation;
  });

  const hasAuthProvider = (auth0Client?: Auth0ClientLike | null) => {
    return resolveAuth0Client(auth0Client) !== null;
  };

  const {
    setStatus,
    setToken,
    setIdToken,
    clearAuth,
    getSessionResultFromState,
    setSession,
    captureSessionSnapshot,
    restoreSessionSnapshot,
  } = createAuthSessionState({
    status,
    syncError,
    jwtToken,
    idToken,
    lastActiveTime,
    sessionActiveUntil,
    tokenExpiresAt,
    ifExternalUserId,
    ifClientId,
    givenName,
    familyName,
    organizationId,
    isTokenExpired,
    userContextStore,
  });

  const triggerMfaRedirect = () => {
    if (mfaRedirectPromise) {
      return mfaRedirectPromise;
    }

    mfaRedirectPromise = import('./auth.store')
      .then(({ useAuthStore }) => useAuthStore().redirectToMfaLogin())
      .finally(() => {
        mfaRedirectPromise = null;
      });

    return mfaRedirectPromise;
  };

  const orchestrator = createAuthSessionOrchestrator({
    sessionSyncPromises,
    userContextStore,
    setStatus,
    setSession,
    clearAuth,
    getSessionResultFromState,
    captureSessionSnapshot,
    restoreSessionSnapshot,
    getJwtToken: () => jwtToken.value,
    getIdToken: () => idToken.value,
    getOrganizationId: () => organizationId.value,
    getRequiredOrganizationId: () => requiredOrganizationId.value,
    requiresBasicInfoCollection: () => requiresBasicInfoCollection.value,
    isTokenExpired: isTokenCurrentlyExpired,
    onMfaRequired: triggerMfaRedirect,
  });

  const {
    ensureSession,
    initializeSession,
    completeLoginCallback,
    refreshAuthClaims,
    refreshCachedAuthSession,
    waitForAuthSync,
    getValidToken,
  } = orchestrator;

  const setLastActiveTime = (ts?: number) => {
    lastActiveTime.value = ts ?? Date.now();
  };

  const setSessionActiveUntil = (ts: number | null) => {
    sessionActiveUntil.value = ts;
  };

  const completeOrganizationSelection = async (
    organizationId: string,
    options?: Omit<EnsureSessionOptions, 'reason' | 'targetOrganizationId'>,
  ): Promise<EnsureSessionResult> => {
    return ensureSession({
      ...options,
      reason: 'organization-selection',
      targetOrganizationId: organizationId,
      fallbackMode: 'preserve-existing-session',
    });
  };

  const completeBasicInfoCollection = ({
    firstName,
    lastName,
  }: {
    firstName: string;
    lastName: string;
  }) => {
    const nextGivenName = firstName.trim();
    const nextFamilyName = lastName.trim();

    givenName.value = nextGivenName.length > 0 ? nextGivenName : null;
    familyName.value = nextFamilyName.length > 0 ? nextFamilyName : null;

    if (requiresBasicInfoCollection.value) {
      setStatus('awaiting-basic-info');
      return;
    }

    if (requiresOrganizationCreation.value) {
      setStatus('awaiting-organization-creation');
      return;
    }

    setStatus(requiresOrganizationSelection.value ? 'awaiting-organization-selection' : 'ready');
  };

  return {
    // State
    status,
    syncError,
    jwtToken: jwtToken as Ref<string | null>,
    idToken: idToken as Ref<string | null>,
    lastActiveTime,
    sessionActiveUntil,
    tokenExpiresAt,
    ifExternalUserId,
    ifClientId,
    givenName,
    familyName,
    organizationId,
    // Computed
    isLoggedIn,
    hasValidToken,
    isReady,
    isAuthenticatedInIf,
    isTokenExpired,
    requiresBasicInfoCollection,
    requiredOrganizationId,
    needsOrganizationScopedSession,
    isOrganizationScoped,
    requiresOrganizationCreation,
    requiresOrganizationSelection,
    // Actions
    setToken,
    setIdToken,
    clearAuth,
    initializeSession,
    completeLoginCallback,
    ensureSession,
    refreshAuthClaims,
    refreshCachedAuthSession,
    completeBasicInfoCollection,
    completeOrganizationSelection,
    hasAuthProvider,
    waitForAuthSync,
    getValidToken,
    setLastActiveTime,
    setSessionActiveUntil,
  };
});
